/**
 * Life Blueprint V2.9.9 — Migration Batch 01 (Sheet27: B33 / B34 / B35)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * STRICT SCOPE
 *   This file implements ONLY the three audited Sheet27 cells listed below.
 *   No other Sheet27 rules, no downstream Sheet28/29/30 logic, no upstream
 *   generators of B5 / B13 / E20 are implemented here.
 *
 * GOLDEN MASTER PROVENANCE (audited)
 *   Sheet27!B33  =IF($B$13<E20,$B$5-1,$B$5)
 *   Sheet27!B34  =MOD(B33-4,10)+1
 *   Sheet27!B35  =MOD(B33-4,12)+1
 *
 *   Audited sample:
 *     B5  = 1977
 *     B13 = 04/02/1977 16:15:00
 *     E20 = 04/02/1977        (i.e. 04/02/1977 00:00:00)
 *   Expected outputs:
 *     B33 = 1977
 *     B34 = 4
 *     B35 = 6
 *
 * INPUT CONTRACT (Batch 01 only — NOT a final public API)
 *   The three upstream values arrive as pre-validated Golden Master values.
 *   Field names are intentionally suffixed with their Excel cell id so
 *   provenance stays visible until the true public field names are audited.
 *
 *     upstreamYear_B5         : integer               (Sheet27!$B$5)
 *     upstreamDateTime_B13    : Date  (full datetime) (Sheet27!$B$13)
 *     upstreamBoundaryDate_E20: Date  (start-of-day)  (Sheet27!E20)
 *
 * BUSINESS MEANING
 *   The business meaning of B34 and B35 is deliberately NOT labelled in this
 *   module because that meaning has not been independently evidenced yet.
 *   Do NOT rename these fields to labels such as "天干" / "地支" / anything
 *   else until the Golden Master audit confirms the semantics.
 *
 * DETERMINISM
 *   No randomness, no wall clock. Given identical input this function
 *   returns byte-wise identical output on every run.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Excel-compatible MOD for a positive divisor.
 *
 * Excel: `MOD(n, d)` returns a value whose sign matches `d`.
 *        For `d > 0` the result is always in `[0, d)`.
 * JS:    the built-in `%` returns a value whose sign matches the dividend `n`,
 *        so `-3 % 10 === -3`, whereas Excel `MOD(-3, 10) === 7`.
 *
 * B33 - 4 can be negative for small years, so this helper is required —
 * the raw `%` operator MUST NOT be used for these cells.
 *
 * Only positive divisors are permitted here because that is all the Sheet27
 * formulas need; a divisor of zero or negative would indicate a caller bug.
 *
 * @param {number} n
 * @param {number} d must be a positive integer
 * @returns {number} n mod d, in [0, d)
 */
export function excelMod(n, d) {
  if (!Number.isInteger(d) || d <= 0) {
    throw new RangeError(
      `[Batch01 · excelMod] divisor must be a positive integer, got ${d}`
    );
  }
  if (!Number.isFinite(n)) {
    throw new TypeError(`[Batch01 · excelMod] dividend must be finite, got ${n}`);
  }
  return ((n % d) + d) % d;
}

/**
 * Compute Sheet27 cells B33, B34, B35 from validated upstream Golden Master
 * inputs. Preserves Excel datetime comparison semantics for B13 < E20.
 *
 * @param {{
 *   upstreamYear_B5: number,
 *   upstreamDateTime_B13: Date,
 *   upstreamBoundaryDate_E20: Date
 * }} slice
 * @returns {{
 *   B33: number,
 *   B34: number,
 *   B35: number,
 *   _provenance: {
 *     excelSources: string[],
 *     formulas: Object,
 *     inputCitations: Object,
 *     note: string
 *   }
 * }}
 */
export function computeBatch01Sheet27(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch01] slice must be an object");
  }
  const { upstreamYear_B5, upstreamDateTime_B13, upstreamBoundaryDate_E20 } = slice;

  if (!Number.isInteger(upstreamYear_B5)) {
    throw new TypeError(
      "[Batch01] upstreamYear_B5 (Sheet27!$B$5) must be an integer year"
    );
  }
  if (!(upstreamDateTime_B13 instanceof Date) || isNaN(upstreamDateTime_B13.getTime())) {
    throw new TypeError(
      "[Batch01] upstreamDateTime_B13 (Sheet27!$B$13) must be a valid Date"
    );
  }
  if (
    !(upstreamBoundaryDate_E20 instanceof Date) ||
    isNaN(upstreamBoundaryDate_E20.getTime())
  ) {
    throw new TypeError(
      "[Batch01] upstreamBoundaryDate_E20 (Sheet27!E20) must be a valid Date"
    );
  }

  // Excel datetime comparison: strictly less-than on serial numbers.
  // JS Date.getTime() ms since epoch preserves that ordering exactly.
  const B13_lt_E20 =
    upstreamDateTime_B13.getTime() < upstreamBoundaryDate_E20.getTime();

  // Sheet27!B33  =IF($B$13<E20, $B$5-1, $B$5)
  const B33 = B13_lt_E20 ? upstreamYear_B5 - 1 : upstreamYear_B5;

  // Sheet27!B34  =MOD(B33-4, 10) + 1
  const B34 = excelMod(B33 - 4, 10) + 1;

  // Sheet27!B35  =MOD(B33-4, 12) + 1
  const B35 = excelMod(B33 - 4, 12) + 1;

  return {
    B33,
    B34,
    B35,
    _provenance: {
      excelSources: ["Sheet27!B33", "Sheet27!B34", "Sheet27!B35"],
      formulas: {
        "Sheet27!B33": "=IF($B$13<E20,$B$5-1,$B$5)",
        "Sheet27!B34": "=MOD(B33-4,10)+1",
        "Sheet27!B35": "=MOD(B33-4,12)+1",
      },
      inputCitations: {
        upstreamYear_B5: "Sheet27!$B$5",
        upstreamDateTime_B13: "Sheet27!$B$13",
        upstreamBoundaryDate_E20: "Sheet27!E20",
      },
      note:
        "Business meaning of B34/B35 is intentionally NOT labelled; " +
        "no independent evidence has established their semantics yet.",
    },
  };
}
